/**
 * field-errors (2026-11-01, plan #83, Mario's boxes 68–70) — the words a
 * STAFF caller is shown for a field the input rules refused: what to change,
 * never what was typed.
 *
 * ONE TABLE, KEYED ON THE RULE ITSELF. A `pattern` rule's words are looked up
 * by the pattern's exact text, not by a constant's or a field's name: the same
 * name holds different patterns in places (two E.164 phone rules, three email
 * rules), and the same pattern sits under many names. A length, number or
 * item limit needs no row: its words are built from the limit the SCHEMA
 * states (`limitFix`). A choice list's words are its schema values
 * (`choiceFix`).
 *
 * `null` MEANS "NOT A STAFF PROBLEM". An id, a token, a cursor, a file's
 * content or an internal name is filled in by the page or by software, never
 * typed by a person, so a refusal of one is a bug and keeps today's line
 * (Mario, box 69, Q6 "staff-fixable only"). Writing `null` is a decision a
 * reviewer can read; leaving a pattern out is not allowed:
 * `tests/field-fix-words-completeness.test.ts` walks every registered action's
 * input rules and fails on a pattern with no row, with a negative control, and
 * runs every `example` through its own pattern.
 *
 * The words are read by a person in a form ("Phone: use the international
 * format …", the form's label in front) and by Mario's MCP connector, so they
 * start in lower case and name the fix, not the rule. An example is the value
 * the forms already show as a placeholder where they show one.
 */

import {
  E164_PATTERN,
  EMAIL_PATTERN,
  TAG_SLUG_PATTERN,
  UUID_V7_PATTERN,
} from "@layer/canonical-data/schema-primitives.js";

export interface FixWords {
  /** The fix, starting in lower case: "use a date written year-month-day". */
  readonly fix: string;
  /** A value the pattern accepts, and the one `fix` quotes where it quotes one; tested against the pattern. */
  readonly example?: string;
}

/** The words for a field left empty that must be filled in. */
export const FILL_THIS_IN = "fill this in";

/** The words for a field that may be left empty, else must meet `fix` ("empty or valid"). */
export function leaveEmptyOr(fix: string): string {
  return `leave it empty, or ${fix}`;
}

const phone: FixWords = {
  fix: "use the international format, starting with + and the country code (for example +447700900123)",
  example: "+447700900123",
};
const email: FixWords = {
  fix: "use a full email address (for example name@example.com)",
  example: "name@example.com",
};
const date: FixWords = {
  fix: "use a date written year-month-day (for example 2026-11-01)",
  example: "2026-11-01",
};
const dateTime: FixWords = {
  fix: "use a date and time with its time zone (for example 2026-11-01T10:00:00Z)",
  example: "2026-11-01T10:00:00Z",
};
const time: FixWords = {
  fix: "use a 24-hour time (for example 09:30)",
  example: "09:30",
};
const slug: FixWords = {
  fix: "use lower-case letters, digits and dashes, starting and ending with a letter or digit (for example summer-offer)",
  example: "summer-offer",
};
const webAddress: FixWords = {
  fix: "use a full web address starting with https:// (for example https://e-pulsive.co.uk)",
  example: "https://e-pulsive.co.uk",
};
const oneLine: FixWords = {
  fix: "write it on one line",
  example: "November news",
};

export const FIX_WORDS_BY_PATTERN: ReadonlyMap<string, FixWords | null> = new Map<
  string,
  FixWords | null
>([
  // Ids, tokens, cursors and file contents: filled in by the page or by
  // software, never typed.
  [UUID_V7_PATTERN, null],
  ["^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$", null],
  ["^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$", null],
  ["^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-7[0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$", null],
  ["^[0-9a-f]{12}$", null],
  ["^[0-9a-f]{32}$", null],
  ["^[0-9a-fA-F-]{8,64}$", null],
  ["^pm_[A-Za-z0-9_]+$", null],
  ["^seti_[A-Za-z0-9_]+$", null],
  ["^[A-Za-z0-9_-]{1,128}$", null],
  ["^[A-Za-z0-9_-]+$", null],
  ["^[0-9]{1,32}$", null],
  ["^[A-Za-z0-9][A-Za-z0-9_:./=-]{0,199}$", null],
  ["^(?!channel:[a-z_]+:.)(?!.*wamid\\.)[A-Za-z0-9_:.-]{1,128}$", null],
  [
    "^(pitching|pitched|replied|agreed|prepped|session_done|delivered|closed):[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$",
    null,
  ],
  [
    "^(unclassified|triaged|drafting|awaiting_mario|revising|replied|awaiting_them|needs_mario|closed):[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$",
    null,
  ],
  ["^\\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\\d|3[01])T([01]\\d|2[0-3]):[0-5]\\d:[0-5]\\d\\.\\d{3}Z$", null],
  ["^<[^\\r\\n]{1,997}>$", null],
  ["^<[^\\r\\n]{1,3998}>$", null],
  ["^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$", null],
  ["^(?=JVBERi0)(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$", null],
  ["^[a-z]+/[a-z0-9][a-z0-9.+-]{0,126}$", null],
  // Internal names, picked from a list or written by software.
  ["^[a-z0-9_:]+$", null],
  ["^[a-z][a-z0-9_-]*$", null],
  ["^[a-z0-9_]{1,24}$", null],
  ["^[a-z0-9][a-z0-9-]{2,59}$", null],
  ["^[a-z0-9_]{1,60}$", null],
  ["^[a-z][a-z0-9-]*$", null],
  ["^[a-z][a-z0-9_]{2,60}$", null],
  ["^[a-z0-9_-]{1,40}(:[a-z0-9_-]{1,40})?$", null],

  // Phones and emails.
  [E164_PATTERN, phone],
  ["^\\+[1-9]\\d{6,14}$", phone],
  [
    "^\\+?[0-9]{3,20}$",
    { fix: "use digits only, with an optional + at the start (for example +447700900123)", example: "+447700900123" },
  ],
  [EMAIL_PATTERN, email],
  ["^[^@\\s]+@[^@\\s]+\\.[^@\\s]+$", email],

  // Dates and times.
  ["^[0-9]{4}-[0-9]{2}-[0-9]{2}$", date],
  ["^\\d{4}-\\d{2}-\\d{2}$", date],
  ["^20[0-9]{2}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$", date],
  [
    "^[0-9]{4}-(0[1-9]|1[0-2])$",
    { fix: "use a month written year-month (for example 2026-11)", example: "2026-11" },
  ],
  ["^[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}(\\.[0-9]+)?(Z|[+-][0-9]{2}:[0-9]{2})$", dateTime],
  ["^[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}(?:\\.[0-9]+)?(?:Z|[+-][0-9]{2}:?[0-9]{2})$", dateTime],
  ["^([01][0-9]|2[0-3]):[0-5][0-9](:[0-5][0-9])?$", time],
  ["^(?:[01][0-9]|2[0-3]):[0-5][0-9]$", time],
  ["^([01][0-9]|2[0-3]):[0-5][0-9]$", time],

  // Names a person types: slugs, codes, file names.
  ["^[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?$", slug],
  ["^[a-z0-9][a-z0-9-]*[a-z0-9]$|^[a-z0-9]$", slug],
  ["^[a-z0-9]([a-z0-9-]*[a-z0-9])?$", slug],
  ["^[a-z0-9]+(?:-[a-z0-9]+)*$", slug],
  [
    TAG_SLUG_PATTERN,
    { fix: "use lower-case letters, digits and dashes, starting with a letter or digit (for example summer-offer)", example: "summer-offer" },
  ],
  [
    "^[a-z0-9][a-z0-9-]{0,63}$",
    { fix: "use lower-case letters, digits and dashes, starting with a letter or digit (for example summer-offer)", example: "summer-offer" },
  ],
  [
    "^[a-z0-9][a-z0-9-]{0,99}$",
    { fix: "use lower-case letters, digits and dashes, starting with a letter or digit (for example summer-offer)", example: "summer-offer" },
  ],
  [
    "^[a-z0-9][a-z0-9-]{0,38}$",
    { fix: "use up to 39 lower-case letters, digits and dashes, starting with a letter or digit (for example summer-offer)", example: "summer-offer" },
  ],
  [
    "^[a-z][a-z0-9-]{0,59}$",
    { fix: "use lower-case letters, digits and dashes, starting with a letter (for example opening-hours)", example: "opening-hours" },
  ],
  [
    "^[a-z][a-z0-9_]{0,63}$",
    { fix: "use lower-case letters, digits and underscores, starting with a letter (for example welcome_offer)", example: "welcome_offer" },
  ],
  [
    "^[a-z][a-z0-9_]{0,99}(\\.terminal)?$",
    { fix: "use an action's name, in lower case with underscores (for example book_session)", example: "book_session" },
  ],
  [
    "^(ok|error|pending|error:[a-z][a-z0-9_]{0,63})$",
    { fix: "use ok, pending, error, or error: followed by the code (for example error:input_invalid)", example: "error:input_invalid" },
  ],
  [
    "^[0-9]{4}-[0-9]{2}-[0-9]{2}-[a-z0-9]+(-[a-z0-9]+)*$",
    { fix: "use the newsletter's name as it is listed: a date, then words joined by dashes (for example 2026-11-01-november-news)", example: "2026-11-01-november-news" },
  ],
  [
    "^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$",
    { fix: "use letters, digits, dashes or underscores, starting with a letter or digit (for example SUMMER10)", example: "SUMMER10" },
  ],
  [
    "^[A-Za-z0-9][A-Za-z0-9_-]{2,63}$",
    { fix: "use 3 to 64 letters, digits, dashes or underscores, starting with a letter or digit (for example SUMMER10)", example: "SUMMER10" },
  ],
  [
    "^[0-9]{6}$",
    { fix: "use the 6-digit code from the message (for example 123456)", example: "123456" },
  ],
  [
    "^\\d{1,20}$",
    { fix: "use digits only (for example 1234567890)", example: "1234567890" },
  ],
  [
    "^-?[0-9]{1,20}$",
    { fix: "use the chat's number as Telegram shows it: digits, with a minus sign in front for a group (for example -1001234567890)", example: "-1001234567890" },
  ],
  [
    "^[a-z]{3}$",
    { fix: "use a three-letter currency code in lower case (for example gbp)", example: "gbp" },
  ],
  [
    "^[A-Za-z]{3}$",
    { fix: "use a three-letter currency code (for example GBP)", example: "GBP" },
  ],
  [
    "^[a-zA-Z]{3}$",
    { fix: "use a three-letter currency code (for example GBP)", example: "GBP" },
  ],
  [
    "^[a-z0-9][a-z0-9._-]{0,118}\\.[a-z0-9]{1,8}$",
    { fix: "use a file name in lower case, with its extension and no spaces (for example logo-dark.png)", example: "logo-dark.png" },
  ],
  [
    "^[A-Za-z0-9][A-Za-z0-9._-]{0,119}$",
    { fix: "use a file name of letters, digits, dots, dashes or underscores, with no spaces (for example invoice-123.pdf)", example: "invoice-123.pdf" },
  ],
  [
    "^[A-Za-z0-9][A-Za-z0-9._-]{0,115}\\.pdf$",
    { fix: "use a file name ending in .pdf, with no spaces (for example price-list.pdf)", example: "price-list.pdf" },
  ],

  // Web addresses.
  ["^https://", webAddress],
  ["^https://\\S+$", webAddress],
  ["^https://.+", webAddress],
  ["^https://[^\\s]+$", webAddress],
  ["^https://[^\\s<>\"]+$", webAddress],

  // Free text with a guard.
  ["^[^\\r\\n]+$", oneLine],
  ["^[^\\r\\n\\t]+$", { fix: "write it on one line, without tabs", example: "November news" }],
  [
    "^[^{}\\r\\n]*\\S[^{}\\r\\n]*$",
    { fix: "write it on one line, without { or } (for example November news)", example: "November news" },
  ],
  ["\\S", { fix: "write something here: spaces alone don't count", example: "Yes" }],
  ["[^\\s.]", { fix: "write some words here: spaces and dots alone don't count", example: "A new class" }],
  [
    "^[^\\u0000\\uD800-\\uDFFF]*$",
    { fix: "remove the broken character, such as half of an emoji left by a paste", example: "Hello" },
  ],
  [
    "^[^\\s\\u0000-\\u001f\\u007f]+(?: [^\\s\\u0000-\\u001f\\u007f]+){0,9}$",
    { fix: "use up to 10 words with one space between them and no line breaks (for example ems training)", example: "ems training" },
  ],
]);

/** The words for a length, number or item limit the schema states; `null` for any other keyword. */
export function limitFix(keyword: string, limit: number): string | null {
  const s = (n: number, one: string, many: string) => (n === 1 ? one : many);
  switch (keyword) {
    case "minLength":
      return limit <= 1 ? FILL_THIS_IN : `use at least ${String(limit)} characters`;
    case "maxLength":
      return `use at most ${String(limit)} ${s(limit, "character", "characters")}`;
    case "minimum":
      return `use a number no lower than ${String(limit)}`;
    case "maximum":
      return `use a number no higher than ${String(limit)}`;
    case "exclusiveMinimum":
      return `use a number higher than ${String(limit)}`;
    case "exclusiveMaximum":
      return `use a number lower than ${String(limit)}`;
    case "minItems":
      return limit <= 1 ? "add at least one" : `add at least ${String(limit)}`;
    case "maxItems":
      return `use at most ${String(limit)}`;
    default:
      return null;
  }
}

const MAX_CHOICES = 30;
const MAX_CHOICE_CHARS = 60;

/** The words for a choice list: its values when they are few and short, else the general line. */
export function choiceFix(allowed: unknown): string {
  const values = Array.isArray(allowed) ? allowed : [];
  const words = values.map((v) =>
    typeof v === "string" && v.length > 0 && v.length <= MAX_CHOICE_CHARS && !/[\r\n]/.test(v)
      ? v
      : typeof v === "number" && Number.isFinite(v)
        ? String(v)
        : null,
  );
  return words.length > 0 && words.length <= MAX_CHOICES && words.every((w) => w !== null)
    ? `choose one of: ${words.join(", ")}`
    : "choose one of the options offered";
}
